const OUTDOOR_SUN_TIMES = Object.freeze(['08:00', '10:00', '12:00', '14:00', '18:00']);
const DEFAULT_OUTDOOR_SUN_TIME = '12:00';
const DEFAULT_OUTDOOR_SUN_LOCATION = Object.freeze({
  latitude: 47.3769,
  longitude: 8.5417,
  source: 'default-zurich'
});

function isValidOutdoorSunTime(value) {
  return OUTDOOR_SUN_TIMES.includes(String(value || '').trim());
}

function isSatelliteFloorplan(floorplan = null) {
  if (!floorplan || typeof floorplan !== 'object') return false;
  const kind = String(floorplan.kind || floorplan.backgroundType || '').trim().toLowerCase();
  const provider = String(floorplan.provider || '').trim().toLowerCase();
  return kind === 'satellite' || provider === 'swisstopo' || provider === 'esri';
}

function numericCoordinate(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function outdoorSunLocationFromFloorplan(floorplan = null) {
  if (!isSatelliteFloorplan(floorplan)) return { ...DEFAULT_OUTDOOR_SUN_LOCATION };
  const latitudes = [
    floorplan.northWestLat,
    floorplan.northEastLat,
    floorplan.southWestLat,
    floorplan.southEastLat
  ].map(numericCoordinate).filter(value => value !== null);
  const longitudes = [
    floorplan.northWestLng,
    floorplan.northEastLng,
    floorplan.southWestLng,
    floorplan.southEastLng
  ].map(numericCoordinate).filter(value => value !== null);
  if (!latitudes.length || !longitudes.length) return { ...DEFAULT_OUTDOOR_SUN_LOCATION };
  return {
    latitude: latitudes.reduce((sum, value) => sum + value, 0) / latitudes.length,
    longitude: longitudes.reduce((sum, value) => sum + value, 0) / longitudes.length,
    source: 'map'
  };
}

function normalizeOutdoorSunSimulation(data = {}, floorplan = null) {
  const source = data && typeof data === 'object' ? data : {};
  const location = outdoorSunLocationFromFloorplan(floorplan);
  return {
    enabled: source.enabled !== false,
    selectedTime: isValidOutdoorSunTime(source.selectedTime) ? source.selectedTime : DEFAULT_OUTDOOR_SUN_TIME,
    latitude: location.latitude,
    longitude: location.longitude,
    source: location.source
  };
}

function outdoorSunSourceLabel(settings = {}) {
  return settings.source === 'map' ? 'Position aus Karte' : 'Default: Zürich';
}

function dateForOutdoorSunTime(time, baseDate = new Date()) {
  const selected = isValidOutdoorSunTime(time) ? time : DEFAULT_OUTDOOR_SUN_TIME;
  const [hours, minutes] = selected.split(':').map(value => Number.parseInt(value, 10) || 0);
  return new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate(),
    hours,
    minutes,
    0,
    0
  );
}

function dayOfYear(date) {
  const start = Date.UTC(date.getFullYear(), 0, 0);
  const current = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.max(1, Math.floor((current - start) / 86400000));
}

function normalizeRadians(value) {
  const twoPi = Math.PI * 2;
  return ((value % twoPi) + twoPi) % twoPi;
}

function calculateOutdoorSunPosition(settings = {}, baseDate = new Date()) {
  const latitude = Number.isFinite(Number(settings.latitude)) ? Number(settings.latitude) : DEFAULT_OUTDOOR_SUN_LOCATION.latitude;
  const longitude = Number.isFinite(Number(settings.longitude)) ? Number(settings.longitude) : DEFAULT_OUTDOOR_SUN_LOCATION.longitude;
  const date = dateForOutdoorSunTime(settings.selectedTime, baseDate);
  const minutesOfDay = date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
  const decimalHour = minutesOfDay / 60;
  const yearDay = dayOfYear(date);
  const gamma = (2 * Math.PI / 365) * (yearDay - 1 + (decimalHour - 12) / 24);
  const equationOfTime = 229.18 * (
    0.000075
    + 0.001868 * Math.cos(gamma)
    - 0.032077 * Math.sin(gamma)
    - 0.014615 * Math.cos(2 * gamma)
    - 0.040849 * Math.sin(2 * gamma)
  );
  const declination = (
    0.006918
    - 0.399912 * Math.cos(gamma)
    + 0.070257 * Math.sin(gamma)
    - 0.006758 * Math.cos(2 * gamma)
    + 0.000907 * Math.sin(2 * gamma)
    - 0.002697 * Math.cos(3 * gamma)
    + 0.00148 * Math.sin(3 * gamma)
  );
  const timezoneHours = -date.getTimezoneOffset() / 60;
  const trueSolarMinutes = minutesOfDay + equationOfTime + 4 * longitude - 60 * timezoneHours;
  const hourAngle = ((trueSolarMinutes / 4) - 180) * Math.PI / 180;
  const latitudeRad = latitude * Math.PI / 180;
  const cosZenith = Math.max(-1, Math.min(1,
    Math.sin(latitudeRad) * Math.sin(declination)
    + Math.cos(latitudeRad) * Math.cos(declination) * Math.cos(hourAngle)
  ));
  const elevation = Math.asin(cosZenith);
  const azimuth = normalizeRadians(
    Math.atan2(
      Math.sin(hourAngle),
      Math.cos(hourAngle) * Math.sin(latitudeRad) - Math.tan(declination) * Math.cos(latitudeRad)
    ) + Math.PI
  );
  return {
    date,
    latitude,
    longitude,
    azimuth,
    elevation,
    azimuthDeg: azimuth * 180 / Math.PI,
    elevationDeg: elevation * 180 / Math.PI
  };
}

function outdoorSunLightVector(settings = {}, baseDate = new Date()) {
  const position = calculateOutdoorSunPosition(settings, baseDate);
  const visibleElevation = Math.max(position.elevation, 5 * Math.PI / 180);
  const horizontal = Math.cos(visibleElevation);
  return {
    ...position,
    x: horizontal * Math.sin(position.azimuth),
    y: Math.sin(visibleElevation),
    z: horizontal * Math.cos(position.azimuth)
  };
}

export {
  OUTDOOR_SUN_TIMES,
  DEFAULT_OUTDOOR_SUN_TIME,
  DEFAULT_OUTDOOR_SUN_LOCATION,
  isValidOutdoorSunTime,
  isSatelliteFloorplan,
  outdoorSunLocationFromFloorplan,
  normalizeOutdoorSunSimulation,
  outdoorSunSourceLabel,
  calculateOutdoorSunPosition,
  outdoorSunLightVector
};
